import ContentPage from '@/components/feature/ContentPage';

export default function WarehousePage() {
  return (
    <ContentPage
      title="CSSBuy Warehouse: Storage, QC and Shipping Guide"
      metaTitle="CSSBuy Warehouse Guide: Storage, Inspection & Shipping | CSSBuyBR"
      metaDescription="CSSBuy warehouse guide. Learn about storage periods, QC inspection, parcel preparation and shipping from warehouse. Independent resource."
      subtitle="Understanding how CSSBuy warehouse works helps you plan purchases and shipping more effectively."
      showToc
      sections={[
        { id: 'storage', title: 'Storage Period' },
        { id: 'qc', title: 'QC Inspection' },
        { id: 'preparation', title: 'Parcel Preparation' },
        { id: 'shipping', title: 'Shipping from Warehouse' },
        { id: 'faq', title: 'FAQ' },
      ]}
      content={
        <div className="space-y-6">
          <section id="storage">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Warehouse Storage Period</h2>
            <p>CSSBuy warehouse stores your purchased items before they are shipped to you. Storage periods vary based on your account type and plan:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Free storage is typically available for a limited period (commonly 30-90 days)</li>
              <li>Extended storage may incur daily fees after the free period</li>
              <li>Items stored too long may be discarded according to platform policies</li>
              <li>Plan your purchases to consolidate shipping and avoid storage fees</li>
            </ul>
          </section>

          <section id="qc">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Quality Control Inspection</h2>
            <p>When items arrive at the warehouse, CSSBuy can perform quality control inspections. This service includes:</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              {[
                'Detailed photos of each item received',
                'Verification of product condition and appearance',
                'Checking for defects, stains, or damage',
                'Size and color verification against your order',
                'Packaging condition assessment',
                'Weight confirmation for shipping estimates',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-background-100 text-sm text-foreground-700">
                  <i className="ri-camera-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p>Review QC photos carefully before approving items for international shipping. If problems are found, you can request returns or exchanges before the items leave the warehouse.</p>
          </section>

          <section id="preparation">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Parcel Preparation</h2>
            <p>Before shipping, CSSBuy prepares your package with several options:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Consolidation — combining multiple items into one package</li>
              <li>Repackaging — removing unnecessary boxes to reduce volume</li>
              <li>Reinforcement — adding protective materials for fragile items</li>
              <li>Labeling — customs declarations and shipping labels</li>
              <li>Insurance — optional coverage for loss or damage</li>
            </ul>
            <p className="mt-2">Choosing the right preparation options can significantly reduce shipping costs and protect your items during transit.</p>
          </section>

          <section id="shipping">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Shipping from Warehouse</h2>
            <p>Once you approve your items and select shipping preferences, CSSBuy dispatches your package. The process involves:</p>
            <ol className="list-decimal list-inside space-y-1.5 ml-2">
              <li>Select your preferred shipping carrier and method</li>
              <li>Confirm your delivery address and contact details</li>
              <li>Pay for shipping and any applicable fees</li>
              <li>CSSBuy prepares and dispatches your package</li>
              <li>Track your shipment through the provided tracking number</li>
              <li>Receive and inspect your package upon arrival</li>
            </ol>
          </section>

          <section id="faq">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Warehouse FAQ</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">What happens if I do not ship my items within the free storage period?</h3>
                <p className="text-sm">Items may start incurring daily storage fees. After an extended period, unclaimed items may be discarded according to platform policies. Always check current terms on the official website.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">Can I return items from the warehouse?</h3>
                <p className="text-sm">Returns depend on the seller\'s policy and the item condition. Contact CSSBuy support promptly if you need to return an item after QC reveals issues.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">Can I combine orders from different sellers into one shipment?</h3>
                <p className="text-sm">Yes, this is one of the main benefits of using a warehouse service. Items from multiple sellers can be consolidated into a single package for more efficient shipping.</p>
              </div>
            </div>
          </section>

          <div className="mt-6 p-4 rounded-xl bg-primary-50 border border-primary-200/70">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">Manage Your Warehouse</h3>
            <p className="text-sm text-primary-700 mb-3">Visit the main site to access your warehouse dashboard and manage your items.</p>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              Visit Main Site
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      }
    />
  );
}